// After registering, and whenever the registrant comes back: the code(s), live hand-off and badge
// status, and the changes they may make themselves (handoff rules: hotel names until the cutoff,
// flights never renamed, cancellations go to the events team).
import React, { useEffect, useState } from "react";
import { api } from "../lib/api.js";

const label = {
  queued: ["Sending…", ""], received: ["With the agent", "ok"], queried: ["Agent has a question", "bad"], used: ["Booked", "ok"],
  confirmed: ["Badge confirmed", "ok"], failed: ["Badge failed · events team notified", "bad"],
};
function Status({ s }) { const [text, tone] = label[s] ?? [s, ""]; return <span className={`status ${tone}`}>{text}</span>; }

export default function Done({ registration, onRestart }) {
  const [reg, setReg] = useState(registration);
  const [editing, setEditing] = useState(null); // traveller id
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const settled = reg.rooms.every(r => r.agentStatus !== "queued") && reg.badges.every(b => b.perensoStatus !== "queued");
  const refresh = async () => { try { setReg(await api(`/registrations/${reg.id}`)); } catch { /* keep last */ } };
  useEffect(() => { if (!reg.changes) refresh(); }, []);                     // the POST response has no traveller ids yet
  useEffect(() => { if (settled) return; const t = setInterval(refresh, 3000); return () => clearInterval(t); }, [reg.id, settled]);

  const ch = reg.changes ?? {};
  const canEditNames = ch.namesOpen && !ch.flightLocked;

  function startEdit(t) { setEditing(t.id); setForm({ name: t.name, email: t.email, phone: t.phone ?? "" }); setError(null); setNotice(null); }
  async function save(t) {
    setBusy(true); setError(null);
    try {
      const body = {};
      if (canEditNames && form.name.trim() !== t.name) body.name = form.name.trim();
      if (ch.namesOpen && form.email.trim() !== t.email) body.email = form.email.trim();
      if ((form.phone ?? "") !== (t.phone ?? "")) body.phone = form.phone.trim() || null;
      if (!Object.keys(body).length) { setEditing(null); return; }
      setReg(await api(`/registrations/${reg.id}/travellers/${t.id}`, { method: "PUT", body }));
      setEditing(null);
      setNotice(body.name ? "Name changed. The agent and the badge are being updated; no new code is needed." : "Saved.");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function cancel(t) {
    const reason = window.prompt(`Ask the events team to cancel ${t.name}'s seat. Why? (optional)`);
    if (reason === null) return;
    setBusy(true); setError(null);
    try { setReg(await api(`/registrations/${reg.id}/travellers/${t.id}/cancel`, { method: "POST", body: { reason } })); setNotice("Sent to the events team. They will decide and get back to you."); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  return (
    <>
      <p className="eyebrow">You're registered</p>
      <h1>Almost there. One call to finish.</h1>
      <p className="lead">Lipari is paying, but Gateway Travel books the flights and the room. Call them with your code; they can see it already and will check your name against it before booking anything.</p>
      {notice && <div className="notice">{notice}</div>}
      {error && <div className="error" role="alert">{error}</div>}

      {reg.rooms.map(r => (
        <div className="card" key={r.id}>
          <div className="code">{r.code}</div>
          <div className="row"><span>Travel agent</span><Status s={r.agentStatus} /></div>
          {r.travellers.map(t => {
            const tr = typeof t === "string" ? { name: t } : t;
            const pending = ch.pendingCancellations?.includes(tr.id);
            return (
              <div className="traveller-line" key={tr.id ?? tr.name}>
                {editing === tr.id ? (
                  <div className="edit">
                    <label>Name<input type="text" value={form.name} disabled={!canEditNames} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
                    {!canEditNames && ch.flightLocked && <div className="help">Flights are non-refundable, so the name on a flight can't change. If {tr.name} can't travel, ask the events team to cancel the seat.</div>}
                    {!canEditNames && !ch.flightLocked && <div className="help">Name changes closed on {ch.cutoffLabel}. Contact the events team.</div>}
                    <label>Email<input type="email" value={form.email} disabled={!ch.namesOpen} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
                    <label>Mobile<input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
                    <div className="edit-actions"><button className="primary" disabled={busy} onClick={() => save(tr)}>Save</button><button className="ghost" onClick={() => setEditing(null)}>Cancel</button></div>
                  </div>
                ) : (
                  <div className="row">
                    <span><b>{tr.name}</b>{tr.email && <><br /><small className="help">{tr.email}{tr.phone ? ` · ${tr.phone}` : ""}</small></>}{pending && <><br /><small className="status">Cancellation requested</small></>}</span>
                    {tr.id && !pending && (
                      <span className="actions">
                        <button className="ghost" onClick={() => startEdit(tr)}>Change</button>
                        <button className="ghost" onClick={() => cancel(tr)}>Can't travel</button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {ch.cutoffLabel && <p className="help">{ch.flightLocked ? "Flight names are fixed. " : ""}Hotel names and emails can be changed until {ch.cutoffLabel}. After that, contact the events team.</p>}

      <h2>Badges · Perenso</h2>
      <div className="card">
        {reg.badges.map(b => (
          <div className="row" key={b.id}><span>{b.name}<br /><small className="help">{b.email}</small></span><Status s={b.perensoStatus} /></div>
        ))}
      </div>
      {onRestart && <button className="ghost" onClick={onRestart}>Back</button>}
    </>
  );
}
